
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import * as pdfjsLib from 'pdfjs-dist';

// Initialize PDF.js worker
const initializeWorker = () => {
  const workerUrl = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).href;
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
};

initializeWorker();

interface UseInvoiceUploadProps {
  onVinDetected: (vin: string) => void;
}

interface TextItem {
  str: string;
  x: number;
  y: number;
  fontName?: string;
  fontSize?: number;
}

function groupTextItemsByPosition(items: TextItem[]): string[] {
  // Sort items by y-coordinate (primary) and x-coordinate (secondary)
  const sortedItems = [...items].sort((a, b) => {
    const yDiff = a.y - b.y;
    return yDiff !== 0 ? yDiff : a.x - b.x;
  });

  // Group items into lines (items with similar y-coordinates)
  const lines: TextItem[][] = [];
  let currentLine: TextItem[] = [];
  let currentY: number | null = null;
  const Y_THRESHOLD = 5; // Allow 5 units of variance in y-position for same line

  sortedItems.forEach(item => {
    if (currentY === null || Math.abs(item.y - currentY) > Y_THRESHOLD) {
      if (currentLine.length > 0) {
        lines.push(currentLine);
      }
      currentLine = [item];
      currentY = item.y;
    } else {
      currentLine.push(item);
    }
  });

  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  // Convert lines to strings, preserving spacing based on x-coordinates
  return lines.map(line => {
    // Sort items in line by x-coordinate
    const sortedLine = line.sort((a, b) => a.x - b.x);
    return sortedLine.map(item => item.str).join('\t');
  });
}

async function extractTextFromPDF(file: File): Promise<string> {
  const startTime = performance.now();
  console.log(`Starting PDF extraction for file: ${file.name} (${Math.round(file.size / 1024)}KB)`);
  
  try {
    const arrayBuffer = await file.arrayBuffer();
    console.log(`ArrayBuffer creation took: ${Math.round(performance.now() - startTime)}ms`);

    const loadingTask = pdfjsLib.getDocument({ 
      data: arrayBuffer,
      useSystemFonts: true,
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.0.269/cmaps/',
      cMapPacked: true,
    });

    const pdf = await loadingTask.promise;
    console.log(`PDF loading took: ${Math.round(performance.now() - startTime)}ms`);

    const maxPages = Math.min(pdf.numPages, 5);
    console.log(`Processing ${maxPages} pages out of ${pdf.numPages} total pages`);

    const pageTexts = [];
    
    for (let i = 1; i <= maxPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      
      // Convert items to our TextItem interface
      const textItems = textContent.items.map((item: any) => ({
        str: item.str,
        x: item.transform ? item.transform[4] : 0, // x coordinate with fallback
        y: item.transform ? item.transform[5] : 0, // y coordinate with fallback
        fontName: item.fontName,
        fontSize: item.fontSize
      }));

      const structuredLines = groupTextItemsByPosition(textItems);
      pageTexts.push(structuredLines.join('\n'));
      
      console.log(`Page ${i} extraction complete with ${structuredLines.length} lines`);
    }

    const extractedText = pageTexts.join('\n\n=== PAGE BREAK ===\n\n');
    console.log(`Complete text extraction took: ${Math.round(performance.now() - startTime)}ms`);
    console.log("Extracted text sample:", extractedText.substring(0, 1000));

    return extractedText;

  } catch (error) {
    console.error("PDF extraction error:", error);
    throw new Error("Failed to extract text from PDF. Please try again.");
  }
}

function preprocessText(text: string): string {
  return text
    .replace(/\n\s+/g, '\n')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s-\s/g, '-')
    .replace(/[^\S\r\n]+/g, ' ')
    .trim();
}

export const useInvoiceUpload = ({ onVinDetected }: UseInvoiceUploadProps) => {
  const [processing, setProcessing] = useState(false);

  const handleInvoiceProcess = async (file: File): Promise<{ success: boolean; error?: string }> => {
    console.log("Starting invoice processing...");
    setProcessing(true);
    
    try {
      // Basic file validation
      if (!file.type.includes('pdf')) {
        throw new Error('Please upload a PDF file');
      }

      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size too large. Please upload a PDF smaller than 10MB.');
      }

      // Get authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to upload invoices');
      }

      // Extract text from PDF
      const extractedText = await extractTextFromPDF(file);
      if (!extractedText) {
        throw new Error('No text could be extracted from the PDF');
      }

      // Process via Edge Function
      const { data, error } = await supabase.functions.invoke('process-invoice', {
        body: { 
          text: extractedText,
          userId: user.id,
          filename: file.name
        }
      });

      console.log("Edge Function response:", data);

      if (error) {
        console.error("Edge Function error:", error);
        throw new Error(error.message || 'Failed to process invoice');
      }

      // Check for VIN in different possible locations in the response
      let vin = null;
      if (data?.analysis?.processedEstimate?.vin) {
        vin = data.analysis.processedEstimate.vin;
      } else if (data?.analysis?.processedEstimate?.vehicle?.vin) {
        vin = data.analysis.processedEstimate.vehicle.vin;
      }

      if (!vin) {
        console.error("No VIN found in response:", data);
        throw new Error("No VIN found in the repair estimate");
      }
      
      // Validate VIN format
      if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) {
        console.error("Invalid VIN format:", vin);
        throw new Error("Invalid VIN format detected");
      }

      console.log("Valid VIN detected:", vin);
      onVinDetected(vin);
      
      // Handle RO Number if present
      const roNumber = data.analysis.processedEstimate.roNumber;
      if (roNumber) {
        console.log("RO Number detected:", roNumber);
        toast.success(`RO Number detected: ${roNumber}`);
      }
      
      return { success: true };

    } catch (error) {
      console.error("Error processing repair estimate:", error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setProcessing(false);
    }
  };

  return {
    processing,
    handleInvoiceProcess
  };
};
